<template>
	<!-- Do not remove this class, AppTabs.vue checks whether to add styles bases upon it -->
	<span class="ai-assistant-factory" :class="{ 'code-block': isCodeblock }">
		<div v-if="!didCompInitialize && isCodeblock" class="width-100 height-100 flex-centerize">
			<AppLoader />
		</div>
		<div class="lottie-loader-container" v-else-if="loadingPrompts">
			<div class="primary-title">
				{{ selectedApiDocsId ? 'AI is learning the API documentation' : 'Creating suggestions based on your data' }}
			</div>
			<Loader class="lottie-loader my-4" showLottie pointerEventsNone />
			<div class="primary-title">This could take a sec</div>
		</div>
		<div class="instructions-screen" v-else-if="!atLeastOneQuestionAndResponse && !openAssistantClicked && isCodeblock">
			<AppIcon class="instruction-header-stars" name="stars"></AppIcon>
			<h3>Let AI write code for you</h3>
			<div class="instructions-steps">
				<div class="box">
					<Col class="py-7">
						<AppIcon class="pb-1" name="1-boxed"></AppIcon>
						<div class="flex flex-row flex-justify-center">
							Map input data on the right
							<AppTooltip
								position="bottom"
								text="Click “Add item” to provide sample values that the AI will use to generate code. Tip: Provide descriptive keys for your values, like “orders”, “ticketID”, etc"
							>
								<AppIcon class="ml-1" name="question-mark-small"></AppIcon>
							</AppTooltip>
						</div>
					</Col>
				</div>
				<div class="box">
					<Col class="pb-4 pr-2 pt-2">
						<AppSwitch style="align-self: flex-end" class="mt-2 mr-2 swtch" v-model="needApiCall" />
						<AppIcon class="pb-1" name="2-boxed"></AppIcon>
						<div class="flex flex-row flex-justify-center">
							Add API documentation
							<AppTooltip
								position="bottom"
								text="APIs are your apps’ gateways for connecting to Ply. The documentation for the APIs can usually be found by searching for the app name and “API”. Try to find the part of the documentation that relates to the specific action you want to perform."
							>
								<AppIcon class="ml-1" name="question-mark-small"></AppIcon>
							</AppTooltip>
						</div>
						<div style="opacity: 0.5; font-size: 0.75rem">Optional, only if you need to make an API call</div>
						<div class="mt-3 mx-5" v-if="needApiCall">
							<AddApiDocumentation
								class="add-api-docs"
								:selectedApiDocs.sync="selectedApiDocsId"
								:apiDocNameChange.sync="selectedApiDocName"
								@deleteApiDoc="deleteApiDoc"
							/>
						</div>
					</Col>
				</div>
			</div>
			<div class="open-assistant">
				<AppTooltip position="bottom" :text="'Open assistant'">
					<PrimaryButton @click="onOpenAssistantClick" class="mb-6">Open assistant</PrimaryButton>
				</AppTooltip>
			</div>
		</div>
		<div v-else :ref="'ai_assistant_ref'" class="text-widget-field">
			<Row v-if="isCodeblock" class="code-block-header">
				<span class="code-title flex">Code writing assistant</span>
				<span class="flex flex-justify-end">
					<span style="cursor: pointer" @click="goToDocsPage" class="documentation-box">
						<AppIcon name="document"></AppIcon>
						<span class="selected-api-doc">{{ `${selectedApiDocName || 'Add API documentation'}` }}</span>
					</span>
					<div class="broom-icon documentation-box" style="cursor: pointer" @click="resetConversation">
						<AppIcon name="broom"></AppIcon>
					</div>
				</span>
			</Row>
			<div v-if="shouldShowSuggestionPrompts" id="code-block-show-suggestion">
				<div class="codeblock-logo-stuff">
					<div class="ai-logo flex-centerize flex-column">
						<AppIcon name="stars"></AppIcon>
						<div>Let AI write code for you</div>
						<div style="opacity: 0.5; font-size: 0.75rem">AI can make mistakes. Consider checking the code.</div>
					</div>
					<Row v-if="shouldShowGenerateSolutionsBtn" @click.once="generateTailoredSolutions" class="gen-tailored-solutions-btn mb-4">
						<AppIcon class="mr-1" name="small-stars-filled"></AppIcon>Generate tailored solutions
					</Row>
					<div class="ai-generated-prompts" ref="ai_generated_prompts">
						<div
							@click.once="suggestionPromptClicked(prompt)"
							class="flex prompt pl-2 mb-1 mr-1"
							:class="{ 'prompt-text-overflowing': isPromptTextOverflowing }"
							:key="i + prompt + i"
							v-for="(prompt, i) in promptOptions"
						>
							<Row class="prompt-row width-100 flex-justify-space-between flex-align-center pr-1">
								{{ prompt }}
								<AppIcon class="up-arrow-filled" name="up-arrow-filled"></AppIcon>
							</Row>
						</div>
					</div>
				</div>
			</div>
			<div v-else class="editor-container display-flex flex-column flex-justify-space-between pt-2">
				<div class="editor-content px-2 mb-1" ref="editor_content">
					<div :key="i" v-for="(message, i) in messagesToShow">
						<div
							class="message-wrapper width-100 flex-row mt-1"
							:class="[message.role === 'assistant' ? 'flex-end--center' : 'flex-start--center']"
						>
							<div class="message-content" :class="[message.role === 'assistant' ? 'ai-message' : 'user-message']">
								<div v-if="isCodeblock">
									<template v-if="message.role === 'assistant'">
										<codemirror v-if="message.content" v-model="message.content" :options="cmOptions" />
									</template>
									<template v-else>{{ message.content }}</template>
								</div>
								<div v-else>
									{{ message.content }}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div v-if="inputDataChanged && atLeastOneQuestionAndResponse">
				<span class="input-data-changed-container mb-2 pa-2 flex">
					<AppIcon name="exclamation-blue" class="exclamation-blue mr-3" />You’ve changed input data, write a new message to update code.
				</span>
			</div>
			<span class="chat-box-span">
				<div class="chat-box">
					<v-textarea
						rows="1"
						auto-grow
						outlined
						hide-details
						:disabled="loading"
						ref="user_text_input"
						class="chat-text-area"
						v-model="textFieldValue"
						@keydown.enter.prevent="onEnter"
						:placeholder="placeholderValue || 'Enter an instruction and get a response from AI'"
					/>
					<div class="chat-submit-btn">
						<AnemicButton
							tile
							@click="submit"
							:loading="loading"
							class="send-button pa-0"
							:disabled="isSubmitDisabled || doesNotMeetWordConditions"
						>
							<AppIcon
								v-if="isCodeblock"
								:name="isSubmitDisabled || doesNotMeetWordConditions ? 'up-arrow-ai-disabled' : 'up-arrow-ai-enabled'"
							/>
							<AppIcon v-else name="send-ai-message" />
						</AnemicButton>
					</div>
				</div>
				<template v-if="isCodeblock && this.atLeastOneQuestionAndResponse">
					<div class="or-line my-4"></div>
					<PrimaryButton class="use-code-btn mb-2" @click="useCodeClicked" :disabled="!isUseCodeBtnEnabled">Use code</PrimaryButton>
				</template>
			</span>
		</div>
	</span>
</template>

<script>
import { isNullOrUndefinedOrEmpty } from '@/utils/helpers';
import { mapGettersShorthand, mapActionsShorthand } from '@/store/utils/mappers';
import { editNode } from '@/store/modules/selectedNode/getters';
import { selectedProjectId } from '@/store/modules/selectedProject/getters';
import 'codemirror/addon/edit/matchbrackets';
import 'codemirror/addon/edit/closebrackets';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/theme/darcula.css';
import { codemirror } from 'vue-codemirror';

import { isEqual } from 'lodash';

import api from '@/api';
import AddApiDocumentation from './ApiDocsComponents/AddApiDocumentation';
import { AI_DEFAULT_MAX_WORDS } from '@/components/BlockEditorWidget/widgetFieldTypes.const';
import WidgetFormService from '@/components/WidgetForm/WidgetFormService';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { fetchAiAssistantApiDocs, deleteAiAssistantApiDoc } from '@/store/modules/aiAssistantApiDocs/actions';
import { useApiDocs } from '@/store/modules/aiAssistantApiDocs/getters';

// Utils
const sleep = async (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const wordCount = (s) => String(s).replace(/-/g, ' ').trim().split(/\s+/g).length;

const PROMPT_MAX_WORDS = 2500;
const SLEEP_TIME = 1100;
const END_INPUT_DELIMITER = '$end_input$';

export default {
	name: 'AIAssistantFactory',
	components: { AddApiDocumentation, codemirror },
	props: {
		selectedApiDocs: { type: Object, default: () => ({}) },
		isCodeblock: { type: Boolean, default: false },
		apiUrl: { type: String },
		allowFineTuningValue: { type: Boolean },
		isRuntime: { type: Boolean },
		isReadOnly: { type: Boolean },
		maxWords: { type: Object },
		placeholder: { type: Object },
		autoSubmitPrefilled: { type: Boolean },
		promptPrefix: { type: Object },
		threadId: { type: String },
		keyValObject: { type: Object },
		// below props are not used by CODEBLOCK
		previousSteps: { type: Object },
		replaceInsertDataStringDict: { type: Object },
		configurationsMappingBlocksUUIDs: { type: Array },
		defaultValue: { type: String },
	},

	computed: {
		...mapGettersShorthand({ editNode, useApiDocs, selectedProjectId }),
		workflowNodeUrlPrefix() {
			return `/workflow-nodes/${this.editNode.id}/`;
		},
		shouldShowSuggestionPrompts() {
			return this.isCodeblock && !this.atLeastOneQuestionAndResponse;
		},
		keyValObjectPrefix() {
			return `input = ${JSON.stringify(this.keyValObject)} ${END_INPUT_DELIMITER}`;
		},
		atLeastOneQuestionAndResponse() {
			return this.messages.length > 1;
		},
		isUseCodeBtnEnabled() {
			return (this.submitClicked || this.atLeastOneQuestionAndResponse) && !this.loading;
		},
		maxWordsEnabled() {
			return this.maxWords.enabled;
		},
		maxWordsValue() {
			return this.maxWordsEnabled ? this.maxWords.value : AI_DEFAULT_MAX_WORDS;
		},
		wordCountStr() {
			// Currently not used, bring back? Not used on purpose? TODO
			if (!this.maxWordsIsANumber) {
				return 'Max words should be a number';
			}
			return this.remainingWords;
		},
		isSubmitDisabled() {
			return !!(!this.allowFineTuningValue && this.submitClicked) || !this.textFieldValue?.length > 0 || !this.isRuntime || this.isReadOnly;
		},
		maxWordsIsANumber() {
			return !isNaN(parseInt(this.maxWordsValue));
		},
		doesNotMeetWordConditions() {
			if (!this.maxWordsIsANumber) {
				return true;
			}
			return wordCount(this.textFieldValue) > this.maxWordsValue || this.maxWordsValue > PROMPT_MAX_WORDS;
		},
		remainingWords() {
			return `${wordCount(this.textFieldValue)} / ${String(this.maxWordsValue)}`;
		},
		placeholderValue() {
			if (!this.placeholder.enabled) {
				return '';
			}
			return this.placeholder.value;
		},

		promptPrefixValue() {
			if (!this.promptPrefix.enabled) {
				return false;
			}
			const { value } = this.promptPrefix;
			return WidgetFormService.processPreviousSteps(
				value,
				this.previousSteps,
				this.replaceInsertDataStringDict,
				this.configurationsMappingBlocksUUIDs,
				false
			);
		},
		messagesToShow() {
			return this.messages.filter((message) => message.role !== 'system');
		},
	},
	async mounted() {
		if (this.isCodeblock) {
			// check if selectedApiDoc exists or was deleted since selected
			if (Object.values(this.useApiDocs)?.length === 0) {
				await this.fetchAiAssistantApiDocs({ projectId: this.selectedProjectId });
			}
			this.clearSelectedApiDocIfNeeded();
			// check if this.threadId before calling setMessagesToThreadMessages (codeBlockParamType is the one who creates threadId)
			if (this.threadId) {
				await this.setMessagesToThreadMessages();
			}
			this.didCompInitialize = true;
			this.scrollToBottomOfChat();
		}
		if (this.promptPrefixValue) {
			this.addMessage('system', this.promptPrefixValue);
		}
		this.textFieldValue = this.defaultValue || '';

		if (!this.isReadOnly && this.isRuntime && this.autoSubmitPrefilled) {
			this.submit();
		}
	},
	methods: {
		...mapActionsShorthand({ fetchAiAssistantApiDocs, deleteAiAssistantApiDoc }),
		checkIfPromptTextOverflowing(retry = false) {
			// change styling if text is overflowing
			const promptsDiv = this.$refs?.ai_generated_prompts;
			if (!promptsDiv && !retry) {
				// this.$refs?.ai_generated_prompts is not yet on the DOM
				this.$nextTick(() => {
					this.checkIfPromptTextOverflowing(true);
					// (Only retries once)
				});
			}
			const children = promptsDiv?.children;
			if (children) {
				for (const child of children) {
					const textIsOverflowing = child.offsetHeight < child.scrollHeight; // if div text/content (including overflowing text) is bigger than div height
					if (textIsOverflowing) {
						this.isPromptTextOverflowing = true;
						return;
					}
				}
			}
		},
		clearSelectedApiDocIfNeeded() {
			if (!(this.selectedApiDocsId in this.useApiDocs)) {
				this.clearSelectedApiDocs();
			}
		},
		async onOpenAssistantClick() {
			this.openAssistantClicked = true;
			const keyValParamIsFilled = !!Object.values(this.keyValObject).filter((val) => !isNullOrUndefinedOrEmpty(val)).length;
			if (this.selectedApiDocsId || keyValParamIsFilled) {
				await this.generateTailoredSolutions();
				this.checkIfPromptTextOverflowing();
			}
		},
		clearSelectedApiDocs() {
			this.$emit('api-docs-changed', { selectedApiDocsId: null, selectedApiDocName: null });
		},
		async deleteApiDoc(apiDocId) {
			await this.deleteAiAssistantApiDoc({ id: apiDocId });
		},
		async goToDocsPage() {
			const value = await AppDialogManagerService.openEditApiDocDialog({ openedFromConvScreen: true });
			this.clearSelectedApiDocIfNeeded();
			if (value) {
				this.selectedApiDocName = value.service_name;
				this.selectedApiDocsId = value.id;
				this.$emit('api-docs-changed', { selectedApiDocsId: this.selectedApiDocsId, selectedApiDocName: this.selectedApiDocName });
			}
		},
		async resetConversation() {
			const res = await AppDialogManagerService.openResetConvApiDocDialog();
			if (!res) {
				return;
			}
			const nodeId = this.editNode.id;
			this.$emit('reset-conversation', nodeId);
			this.didCompInitialize = false;
			this.openAssistantClicked = false;
		},
		async generateTailoredSolutions() {
			this.loadingPrompts = true;
			this.shouldShowGenerateSolutionsBtn = false;
			const origPrompts = [...this.promptOptions];
			const prompt = `The user's data is: ${this.keyValObjectPrefix}`;
			const url = this.buildAssistantUrl('generate_prompts/');
			const payload = { prompt };
			if (this.selectedApiDocsId) {
				payload.api_docs_id = this.selectedApiDocsId;
			}
			const run = await api.invokeAI(url, payload);
			let messages;
			try {
				await this.waitForRunToFinish({ runId: run.run_id, runStatus: run.run_status, generatedPromptsThreadId: run.thread_id });
				messages = await this.listMessagesOfThread(run.thread_id);
			} catch (err) {
				console.error(err);
				this.$toast.error(`Error occurred: ${err.response?.data?.error?.msg || err}`);
			}
			try {
				const parsed = JSON.parse(messages[0].content);
				this.promptOptions = parsed.prompts;
			} catch (error) {
				console.error('Parsing response failed: ', error);
				this.promptOptions = origPrompts;
				throw new Error("Couldn't load generated prompts");
			} finally {
				this.loadingPrompts = false;
			}
		},
		suggestionPromptClicked(val) {
			this.textFieldValue = val;
			this.submit();
		},
		useCodeClicked() {
			this.$emit('use-code-clicked', this.aiResponse);
		},
		scrollToBottomOfChat() {
			this.$nextTick(() => {
				const formBottom = this.$refs.editor_content;
				if (!formBottom) {
					return;
				}
				formBottom.scrollTop = formBottom.scrollHeight;
				this.$refs.user_text_input.focus();
			});
		},
		addMessage(role, content) {
			this.messages.push({
				role,
				content,
			});
		},
		updateLastAssistantMessage(text) {
			this.messages.filter((msg) => msg.role === 'assistant').at(-1).content += text;
		},
		onEnter(e) {
			if (this.isSubmitDisabled || this.doesNotMeetWordConditions) {
				return;
			}
			if (e.shiftKey) {
				this.textFieldValue += '\n';
			} else {
				this.submit();
			}
		},
		buildAssistantUrl(path) {
			return new URL(`${process.env.VUE_APP_API}${this.workflowNodeUrlPrefix}assistant/${path}`);
		},
		async useAiStreaming({ url, payload }) {
			this.addMessage('assistant', '');
			const response = await api.invokeAISSE(url, payload);
			const reader = response.body.getReader();
			let doneReadingStream = false;
			this.aiResponse = '';
			while (!doneReadingStream) {
				const readResponse = await reader.read();
				const { value } = readResponse;
				doneReadingStream = readResponse.done;
				if (doneReadingStream) break;
				const text = new TextDecoder().decode(value);
				this.aiResponse += text;
				this.updateLastAssistantMessage(text);
			}
		},
		async streamUsingAssistant(threadId) {
			const streamingUrl = this.buildAssistantUrl('stream_response/');
			await this.useAiStreaming({ url: streamingUrl, payload: { thread_id: threadId } });
		},
		async listMessagesOfThread(threadId) {
			const url = this.buildAssistantUrl('list_thread_messages/');
			const res = await api.invokeAI(url, { thread_id: threadId });
			return res.messages;
		},
		async addMessageToThread(messageContent) {
			const message = this.keyValObjectPrefix + '\n' + messageContent;
			const url = this.buildAssistantUrl('add_msg_to_thread/');
			const payload = { thread_id: this.threadId, user_msg: message, api_docs_id: this.selectedApiDocsId };
			await api.invokeAI(url, payload);
		},
		stripInputFromUserMsg(userMsg) {
			if (!userMsg.includes(END_INPUT_DELIMITER)) {
				console.error({ userMsg });
				this.toast.error('Unexpected thread messages returned');
				throw new Error('Unexpected thread messages returned');
			}
			return userMsg.split(END_INPUT_DELIMITER).at(-1).trim();
		},
		async setMessagesToThreadMessages() {
			// Only used when isCodeblock
			const messages = await this.listMessagesOfThread(this.threadId);
			this.threadMessagesArr = messages;
			this.messages = messages
				.map((msg) =>
					msg.role === 'user'
						? { ...msg, content: this.stripInputFromUserMsg(msg.content) }
						: msg.role === 'assistant'
						? { ...msg, content: msg.content }
						: msg
				)
				.reverse();
			this.setAiResponseToLastMessage();
			this.scrollToBottomOfChat();
		},
		setAiResponseToLastMessage() {
			if (!this.messages.length || !this.threadMessagesArr.length) {
				return;
			}
			this.aiResponse = this.threadMessagesArr[0].content;
		},
		async waitForRunToFinish(data) {
			const { runId, attempts = 0, generatedPromptsThreadId = null } = data;
			if (attempts > 25) {
				// TODO, should get handled better than this?
				throw new Error(`Run with id ${runId} is taking too long to complete.`);
			}
			// this.threadId is the code generation conv, generatedPromptsThreadId is the conversation asking the AI to generate prompts
			const threadId = generatedPromptsThreadId || this.threadId;
			const url = this.buildAssistantUrl('run_status/');
			const { run_status: currentRunStatus } = await api.invokeAI(url, { run_id: runId, thread_id: threadId });
			if (currentRunStatus !== 'completed') {
				await sleep(SLEEP_TIME);
				await this.waitForRunToFinish({ ...data, attempts: attempts + 1 });
			}
		},
		async codeBlockSubmit() {
			await this.addMessageToThread(this.textFieldValue);
			this.lastUsedKeyValObject = this.keyValObject; // Sets the last used keyValObject to the current one in order to let the user know if input data has changed and he needs to re-run the code with updated inputData
			this.inputDataChanged = false;
			try {
				await this.streamUsingAssistant(this.threadId);
			} catch (err) {
				console.error(err);
				this.$toast.error(`Error occurred: ${err.response?.data?.error?.msg || err}`);
			}
			this.textFieldValue = '';
			this.setAiResponseToLastMessage();
			this.loading = false;
			this.submitClicked = true;
		},
		async submit() {
			if (this.isSubmitDisabled) {
				return;
			}
			if (this.maxWordsValue > PROMPT_MAX_WORDS) {
				this.loading = false;
				this.$toast.error('The value for max words is greater than the allowed value');
				return;
			}
			this.loading = true;
			this.addMessage('user', this.textFieldValue);
			if (this.isCodeblock) {
				await this.codeBlockSubmit();
				return;
			}
			try {
				if (this.isCodeblock) {
					await this.codeBlockSubmit();
				} else {
					this.textFieldValue = '';
					const url = this.apiUrl;
					const { messages } = this;
					await this.useAiStreaming({ url, payload: { messages } });
					this.$emit('valueChanged', this.aiResponse);
					this.loading = false;
					this.submitClicked = true; // if fine-tuning is off submit should be disabled after 1 click
				}
			} catch (err) {
				this.$toast.error(`Error occurred while reading AI Response`);
				console.error(err);
			} finally {
				this.loading = false;
				this.loaded = true;
			}
		},
	},
	data() {
		return {
			isPromptTextOverflowing: false,
			shouldShowGenerateSolutionsBtn: false,
			cmOptions: {
				readOnly: 'nocursor',
				mode: 'javascript',
				theme: 'darcula',
				spellcheck: true,
				lineNumbers: true,
				lineWrapping: true,
				'font-size': '10%',
			},
			promptOptions: [
				'Generate code prompts for this data: ',
				'What code can I use for this input data',
				"Here's my data, what coding options do I have?",
				'Suggest coding tasks for this data: ',
			],
			inputDataChanged: false,
			selectedApiDocsId: this.selectedApiDocs?.id || null,
			selectedApiDocName: this.selectedApiDocs?.service_name || '',
			didCompInitialize: false,
			openAssistantClicked: false,
			needApiCall: false,
			aiResponse: '',
			textFieldValue: '',
			submitClicked: false,
			output: '',
			loaded: false,
			loading: false,
			prompt: '',
			history: [],
			messages: [],
			threadMessagesArr: [],
			lastUsedKeyValObject: this.keyValObject,
			lastUsedApiDocs: null,
			apiDocsChanged: false,
			loadingPrompts: false,
		};
	},
	watch: {
		selectedApiDocsId: {
			handler(newV) {
				this.$emit('api-docs-changed', { selectedApiDocsId: newV, selectedApiDocName: this.selectedApiDocName });
			},
		},
		selectedApiDocs: {
			handler(newV) {
				this.selectedApiDocsId = newV.id;
				this.selectedApiDocName = newV.service_name;

				// Determine whether to show genTailoredSolutions btn
				this.apiDocsChanged = this.selectedApiDocsId !== this.lastUsedApiDocs;
				this.shouldShowGenerateSolutionsBtn = this.inputDataChanged || this.apiDocsChanged;
			},
		},
		threadId: {
			handler() {
				// threadId should only change upon reset-conversation event emitted
				// This happens after creating a new thread and setting it's value in node's params_map
				this.threadMessagesArr = [];
				this.messages = [];
				this.didCompInitialize = true;
			},
		},
		openAssistantClicked: {
			handler() {
				if (this.openAssistantClicked) {
					this.lastUsedApiDocs = this.selectedApiDocsId;
				}
			},
		},
		keyValObject: {
			handler() {
				const isKeyValSameAsInitial = isEqual(this.keyValObject, this.lastUsedKeyValObject);
				this.inputDataChanged = !isKeyValSameAsInitial;
				this.shouldShowGenerateSolutionsBtn = this.inputDataChanged || this.apiDocsChanged;
			},
			deep: true,
		},
	},
};
</script>
<style scoped lang="scss">
.lottie-loader-container {
	margin: 260px auto;
	text-align: center;
}

.input-data-changed-container {
	color: #393939;
	font-size: 14px;
	align-items: center;
	background: #fafbfd 0% 0% no-repeat padding-box;
	border: 1px solid #ebeef4;
	border-radius: 6px;
	opacity: 1;
	height: 35px;
	letter-spacing: 0px;
}
.up-arrow-filled {
	opacity: 0;
	transition: 0.2s;
}
.prompt-row {
	&:hover {
		.up-arrow-filled {
			opacity: 1;
			transition: 0.2s;
		}
	}
	&:active {
		filter: brightness(0.9);
		transition: 0.2s;
	}
}
.ai-logo {
	margin-bottom: auto;
	@media (max-width: 1515px) {
		margin-bottom: 1rem;
	}
}
.gen-tailored-solutions-btn {
	cursor: pointer;
	border: 1px solid #5858ec;
	padding: 2px;
	border-radius: 6px;
	opacity: 1;
	// font: normal normal normal 14px/21px Proxima Nova; // Seems to make it worse and less like design? Address? // TODO
	letter-spacing: 0px;
	color: #5858ec;
	margin-right: auto;
	margin-left: auto;
	padding-right: 0.5rem;
	padding-left: 0.5rem;
	@media (max-width: 1515px) {
		margin-top: auto;
	}
}
.add-api-docs {
	margin-right: -6px;
	margin-bottom: -1.5rem;
}
.code-block-header {
	* {
		padding: 0.3rem;
	}
	.selected-api-doc {
		padding-top: 0.36rem;
		font-size: 15px !important;
	}
	.code-title {
		padding: 1rem 0.5rem;
	}
}

.broom-icon {
	margin-left: 0.7rem;
}

#code-block-show-suggestion {
	display: flex;
	flex-direction: column;
	height: 100%;
	margin-top: 110px;
	@media (max-width: 1515px) {
		margin-top: 1rem;
	}
}

.codeblock-logo-stuff {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	height: 100%;
	flex: flex-wrap;
}

.codeblock-logo-stuff .ai-generated-prompts {
	margin-top: auto;
	display: flex;
	flex-wrap: wrap;
	margin-bottom: 0.5rem;
}

.ai-generated-prompts .prompt {
	width: 300px;
	height: 60px;
	background: #ffffff 0% 0% no-repeat padding-box;
	border: 1px solid #eceef7;
	border-radius: 6px;
	opacity: 1;
	font-size: 80%;
	letter-spacing: 0px;
	color: #262626;
	transition: 0.2s;
	&:hover {
		cursor: pointer;
		background: #eeeffa 0% 0% no-repeat padding-box;
		transition: 0.2s;
	}
}
.prompt-text-overflowing {
	height: unset !important;
	width: unset !important;
}

.text-widget-field {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 100%;
}
.chat-box-span {
	display: flex;
	flex-direction: column;
}
.code-block .chat-box-span {
	margin-right: 0.35rem;
	margin-left: 0.35rem;
	margin-top: -0.5rem;
}

.use-code-btn {
	align-self: flex-end;
}
.box {
	display: flex;
	flex-direction: column;
	border: 1px solid #eceef7;
	background: #ffffff 0% 0% no-repeat padding-box;
	border-radius: 6px;
	opacity: 1;
	margin: 1rem;
}

.documentation-box {
	display: flex;
	border: 1px solid #e4e8f2;
	border-style: solid;
	border-width: 1px;
	background-clip: padding-box;
	border-radius: 10px;
	background: #ffffff 0% 0% no-repeat padding-box;
	opacity: 1;
	&:hover {
		background-color: var(--app-selection-hover-background);
	}
}

.instructions-screen {
	display: flex;
	flex-direction: column;
	text-align: center;
	padding-top: 25px;
	.instruction-header-stars {
		padding-bottom: 11px;
	}
}
.open-assistant {
	position: absolute;
	bottom: 0;
	right: 25%;
	left: 25%;
}

$footer-height: 50px;

.editor-container {
	height: 70%;
	font-family: Poppins;
}
.editor-content {
	overflow: auto;
	height: calc(100% - #{$footer-height});
}
.footer-container {
	height: $footer-height;
	border-top: 1px solid #e9ecf4;
}
.message-content {
	max-width: 80%;
	padding: 14px 12px;
	font-size: 12px;
	white-space: pre-wrap;
	border: 1px solid #e4e8f2;
	color: var(--primary-font-color);
}
.ai-message {
	background: #ebeef0;
	border-radius: 10px 10px 0 10px;
}
.user-message {
	border-radius: 10px 10px 10px 0;
	line-break: anywhere;
}
.send-button {
	width: 32px !important;
	height: 32px !important;
	color: white !important;
	border-radius: 8px !important;
	background: #6060f7 !important;
}

.chat-box {
	position: relative;
	.chat-submit-btn {
		position: absolute;
		bottom: 10px;
		right: 18px;
	}
	.chat-text-area {
		max-height: 100px;
		padding: 4px 10px;
		overflow-y: auto;
		margin-bottom: 1px;
		border-radius: 4px;
		border: 1px solid var(--grey-background);
		background: var(--insert-data-key-background);

		&::v-deep {
			fieldset {
				border: none !important;
				background: transparent !important;
			}
			&.v-textarea {
				padding-right: 65px;
				.v-input__slot {
					padding: 0 !important;
				}
			}
		}
	}
}

.or-line {
	width: 100%;
	text-align: center;
	border-bottom: 1px solid #e9ecf4;
	line-height: 0.1em;
}
</style>
